import React, { useEffect, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { default as CancelIcon, default as CloseIcon } from "@mui/icons-material/Close";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Avatar,
  Box,
  IconButton,
  Modal,
  Button,
  TextField,
  InputLabel,
  styled,
  Tooltip,
  CircularProgress
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import TextsmsOutlinedIcon from "@mui/icons-material/TextsmsOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { MuiFileInput } from "mui-file-input";
import Swal from "sweetalert2";

const apiBaseURL = process.env.REACT_APP_API_BASE_URL;
const appColor = "#59D6DF";
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1
});

const CommentsModal = ({ open, onClose, comments, loading, onDelete, onLoadMore, hasMore }) => {
  const handleScroll = (event) => {
    if (!hasMore || loading || !onLoadMore) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 60) {
      onLoadMore();
    }
  };
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "80vh",
          width: { xs: "92vw", sm: 520 },
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 3,
          overflowY: "auto",
          p: 2
        }}
        onScroll={handleScroll}
      >
        <IconButton
          sx={{ position: "absolute", top: 8, right: 8, color: "black" }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ pt: 4 }}>
          <Typography variant="h6" mb={2}>
            Comments
          </Typography>
          {loading ? (
            <Typography variant="body2">Loading comments...</Typography>
          ) : comments.length === 0 ? (
            <Typography variant="body2">No comments available.</Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {comments.map((comment) => (
                <Box
                  key={comment._id}
                  sx={{
                    display: "flex",
                    gap: 2,
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    p: 1.5
                  }}
                >
                  <Avatar
                    src={comment.user?.profileImage || ""}
                    alt={comment.user?.fullName || "User"}
                    sx={{ width: 36, height: 36, mt: 0.5 }}
                  />
                  <Box flex={1}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} mb={0.5}>
                      {comment.user?.fullName || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {comment.text || ""}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => onDelete && onDelete(comment)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

const Broadcasting = () => {
  const [adminPost, setAdminPost] = useState(null);
  const [open, setOpen] = useState(false);
  const [id, setId] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminProfile, setAdminProfile] = useState("");
  const [postContent, setPostContent] = useState("");
  const [defaultPost, setDefaultPost] = useState("");
  const [defaultPostId, setDefaultPostId] = useState("");
  const [preview, setPreview] = useState(null);
  const [postPreview, setPostPreview] = useState(null);
  const [adminNameError, setAdminNameError] = useState("");
  const [postContentError, setPostContentError] = useState("");
  const [adminProfileError, setAdminProfileError] = useState("");
  const [defaultPostError, setDefaultPostError] = useState("");
  const [deleteCalled, setDeleteCalled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [broadcastComments, setBroadcastComments] = useState([]);
  const [commentsPage, setCommentsPage] = useState(1);
  const [commentsHasMore, setCommentsHasMore] = useState(false);
  const commentsPageSize = 10;

  const handleAdminProfileFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const acceptedTypes = ["png", "jpg", "jpeg"];
      const acceptedTypesLong = ["image/png", "image/jpg", "image/jpeg"];
      const nameExtension = file.name.split(".").pop();
      if (!acceptedTypes.includes(nameExtension) || !acceptedTypesLong.includes(file.type)) {
        setAdminProfileError("Please upload valid admin profile picture.");
        return;
      } else {
        const objectURL = URL.createObjectURL(file);
        setPreview(objectURL);
        setAdminProfile(file);
        setAdminProfileError("");
      }
    }
  };

  const handleAdminPostFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const acceptedTypes = ["png", "jpg", "jpeg"];
      const acceptedTypesLong = ["image/png", "image/jpg", "image/jpeg"];
      const nameExtension = file.name.split(".").pop();
      if (!acceptedTypes.includes(nameExtension) || !acceptedTypesLong.includes(file.type)) {
        setDefaultPostError("Please upload valid admin post picture.");
        return;
      } else {
        const objectURL = URL.createObjectURL(file);
        setPostPreview(objectURL);
        setDefaultPost(file);
        setDefaultPostError("");
      }
    }
  };

  const handleDeleteClick = (_id) => {
    Swal.fire({
      title: `Are you sure you want to delete this post?`,
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonColor: appColor
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            const headers = {
              "Content-Type": "application/json",
              "x-access-token": `${localStorage.getItem("token")}`
            };
            const deleteMoveNow = await axios.delete(`${apiBaseURL}/api/v1/admin/deleteadminpost`, {
              headers: headers,
              data: {
                id
              }
            });
            if (deleteMoveNow.data) {
              Swal.fire("The post is deleted now!", "", "success");
              setDeleteCalled(!deleteCalled);
            }
          } catch (error) {
            console.log(error, "error");
          }
        } else if (result.isDenied) {
          Swal.fire("Changes are not saved", "", "info");
        }
      })
      .catch((error) => {
        console.log(error, "error");
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (adminName == "") {
      setAdminNameError("Please enter Admin Name.");
      return;
    }
    if (postContent == "") {
      setPostContentError("Please enter content for post.");
      return;
    }
    if (!preview && adminProfile == "") {
      setAdminProfileError("Please upload admin profile picture.");
      return;
    }
    if (!postPreview && defaultPost == "") {
      setDefaultPostError("Please upload admin profile picture.");
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("id", id);
    formData.append("adminName", adminName);
    formData.append("postContent", postContent);
    formData.append("profileImage", adminProfile);
    formData.append("defaultPost", defaultPost);
    formData.append("defaultPostId", defaultPostId);

    const headers = {
      "Content-Type": "multipart/form-data",
      "x-access-token": `${localStorage.getItem("token")}`
    };
    axios
      .post(`${apiBaseURL}/api/v1/admin/addupdatebroadcasting`, formData, {
        headers: headers
      })
      .then((res) => {
        if (res.data) {
          const { message } = res.data;
          setOpen(false);
          Swal.fire({
            position: "center",
            icon: "success",
            title: `${message}`,
            showConfirmButton: false,
            timer: 1000
          });
        }
      })
      .catch((error) => {
        console.log(error, "error");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleClose = () => {
    setOpen(false);
    setPreview(null);
    setPostPreview(null);
  };

  useEffect(() => {
    (async () => {
      const headers = {
        "Content-Type": "application/json",
        "x-access-token": `${localStorage.getItem("token")}`
      };
      const broadcastingList = await axios.get(`${apiBaseURL}/api/v1/admin/broadcasting`, {
        headers
      });
      if (broadcastingList.data) {
        setAdminPost(broadcastingList.data.data.adminPost);
        setId(
          broadcastingList.data.data.adminPost?._id ? broadcastingList.data.data.adminPost._id : ""
        );
        setAdminName(broadcastingList.data.data.adminPost?.admin.adminName || "");
        setPostContent(broadcastingList.data.data.adminPost?.content || "");
        setPreview(`${broadcastingList.data.data.adminPost?.admin.profileImage}` || null);
        if ("images" in broadcastingList.data.data.adminPost) {
          setPostPreview(`${broadcastingList.data.data.adminPost?.images[0]?.url}` || null);
          setDefaultPostId(broadcastingList.data.data.adminPost?.images[0]?._id || "");
        }
      }
    })();
  }, [open, deleteCalled]);
  if (!adminPost) return null;

  const { admin, content, images, likes, comments, _id } = adminPost;

  const fetchBroadcastComments = async (page, append = false) => {
    if (!_id) return;
    setCommentsLoading(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        "x-access-token": `${localStorage.getItem("token")}`
      };
      const res = await axios.get(
        `${apiBaseURL}/api/v1/admin/getadminpostcomments/${_id}`,
        {
          headers,
          params: {
            page,
            limit: commentsPageSize
          }
        }
      );
      const data = res?.data?.data || [];
      const meta = res?.data?.meta || {};

      setBroadcastComments((prev) => (append ? [...prev, ...data] : data));
      setCommentsPage(page);
      if (meta && typeof meta.currentPage === "number" && typeof meta.lastPage === "number") {
        setCommentsHasMore(meta.currentPage < meta.lastPage);
      } else {
        setCommentsHasMore(data.length === commentsPageSize);
      }
    } catch (error) {
      console.error("Error fetching admin post comments:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch comments. Please try again."
      });
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleOpenCommentsModal = async () => {
    if (!_id) return;
    setCommentsModalOpen(true);
    setBroadcastComments([]);
    setCommentsHasMore(false);
    await fetchBroadcastComments(1, false);
  };

  const handleCloseCommentsModal = () => {
    setCommentsModalOpen(false);
    setBroadcastComments([]);
    setCommentsPage(1);
    setCommentsHasMore(false);
  };

  const handleDeleteComment = async (comment) => {
    if (!comment?._id) return;

    // Hide comments modal while confirmation dialog is shown
    setCommentsModalOpen(false);

    const result = await Swal.fire({
      title: "Are you sure you want to delete this comment?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonColor: appColor
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        "x-access-token": `${localStorage.getItem("token")}`
      };

      await axios.delete(`${apiBaseURL}/api/v1/admin/deletecomment/${comment._id}`, {
        headers
      });

      // Update local list
      setBroadcastComments((prev) => prev.filter((c) => c._id !== comment._id));
      // Trigger page data refresh so counts stay in sync
      setDeleteCalled((prev) => !prev);

      Swal.fire({
        icon: "success",
        title: "Comment deleted successfully.",
        timer: 1000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error("Error deleting comment:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete comment. Please try again."
      });
    }
  };

  const handleLoadMoreBroadcastComments = async () => {
    if (!commentsHasMore || commentsLoading) return;
    await fetchBroadcastComments(commentsPage + 1, true);
  };

  return images && images.length ? (
    <DashboardLayout>
      <DashboardNavbar />
      <Box
        mb={3}
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          position: "relative"
        }}
      >
        <Button
          sx={{
            color: "#59D6DF",
            border: "1px solid grey",
            right: 0,
            position: "absolute",
            top: 2
          }}
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Add/Update Brodcast
        </Button>
      </Box>
      <Box p={4} sx={{ position: "relative" }}>
        <Box width={"100%"} sx={{ position: "relative", width: "75%" }}>
          <Tooltip title="Delete" sx={{ position: "absolute", right: 40 }}>
            <IconButton
              onClick={() => {
                handleDeleteClick(_id);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Card
            sx={{
              bgcolor: "#000",
              color: "#fff",
              borderRadius: 20,
              backgroundImage: images && images.length ? `url(${images[0]?.url})` : "",
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center center",
              width: "600px",
              height: "600px",
              display: "flex",
              position: "relative"
            }}
          >
            <CardContent sx={{ position: "absolute", bottom: 0, left: 0 }}>
              <Box display="flex" alignItems="center" mb={2} sx={{ zIndex: 0 }}>
                <Avatar src={`${admin.profileImage}`} alt="Admin" variant="rounded" />
                <Box ml={2}>
                  <Typography
                    sx={{ color: "white", fontWeight: 500, fontSize: "24px" }}
                    variant="body1"
                    component="div"
                  >
                    {admin.adminName}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body1" paragraph sx={{ fontWeight: 100, fontSize: "15px" }}>
                {content}
              </Typography>
              <Box display="flex" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <IconButton color="error">
                    <FavoriteIcon />
                  </IconButton>
                  <Typography>{likes?.length || 0}</Typography>
                  <IconButton color="primary" onClick={handleOpenCommentsModal}>
                    <TextsmsOutlinedIcon />
                  </IconButton>
                  <Typography>{comments?.length || 0}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
      <CommentsModal
        open={commentsModalOpen}
        onClose={handleCloseCommentsModal}
        comments={broadcastComments}
        loading={commentsLoading}
        onDelete={handleDeleteComment}
        onLoadMore={handleLoadMoreBroadcastComments}
        hasMore={commentsHasMore}
      />
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "10px"
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 5,
              top: 5
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="h2" gutterBottom>
            {id !== "" ? "Update Broadcast" : "Add Broadcast"}
          </Typography>
          <form autoComplete="off" onSubmit={handleSubmit}>
            <Box mb={2}>
              <TextField
                fullWidth
                label="Admin Name"
                value={adminName}
                variant="outlined"
                margin="normal"
                onChange={(e) => {
                  setAdminName(e.target.value);
                  setAdminNameError("");
                }}
              />
              <InputLabel sx={{ pt: 1, color: "red" }}>{adminNameError}</InputLabel>
            </Box>
            <Box mb={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  component="label"
                  variant=""
                  sx={{
                    border: "1px solid rgba(0,0,0,0.2)",
                    borderRadius: 1,
                    py: 1.5,
                    justifyContent: "start",
                    color: "rgba(0,0,0,0.5)",
                    fontWeight: 450,
                    textTransform: "none",
                    fontSize: "13px",
                    width: preview ? "90%" : "100%"
                  }}
                  tabIndex={-1}
                >
                  Upload Admin Profile
                  <VisuallyHiddenInput type="file" onChange={handleAdminProfileFileChange} />
                </Button>
                {preview && (
                  <Box mt={2} display="flex" justifyContent="center" sx={{ position: "relative" }}>
                    <Avatar
                      src={preview}
                      alt="Admin Profile"
                      sx={{ width: 30, height: 30, position: "absolute", bottom: 5 }}
                    />
                  </Box>
                )}
              </Box>
              <InputLabel sx={{ pt: 1, color: "red" }}>{adminProfileError}</InputLabel>
            </Box>
            <Box mb={2}>
              <TextField
                fullWidth
                label="Post Content"
                variant="outlined"
                rows={3}
                value={postContent}
                multiline
                sx={{
                  mt: 2,
                  "& .MuiInputLabel-root": {
                    backgroundColor: "white",
                    padding: "4px"
                  },
                  "& .MuiInputBase-input": {
                    p: "0px !important"
                  },
                  "& .MuiInputBase-root": {
                    pt: "15px !important",
                    px: 2
                  }
                }}
                onChange={(e) => {
                  setPostContent(e.target.value);
                  setPostContentError("");
                }}
              />
              <InputLabel sx={{ pt: 1, color: "red" }}>{postContentError}</InputLabel>
            </Box>
            <Box mb={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  component="label"
                  variant=""
                  sx={{
                    border: "1px solid rgba(0,0,0,0.2)",
                    borderRadius: 1,
                    py: 1.5,
                    justifyContent: "start",
                    color: "rgba(0,0,0,0.5)",
                    fontWeight: 450,
                    fontSize: "13px",
                    textTransform: "none",
                    width: postPreview ? "90%" : "100%"
                  }}
                  tabIndex={-1}
                >
                  Upload Admin Post
                  <VisuallyHiddenInput type="file" onChange={handleAdminPostFileChange} />
                </Button>
                {postPreview && (
                  <Box mt={2} display="flex" justifyContent="center" sx={{ position: "relative" }}>
                    <Avatar
                      src={postPreview}
                      alt="Admin Post"
                      sx={{ width: 30, height: 30, position: "absolute", bottom: 5 }}
                    />
                  </Box>
                )}
              </Box>
              <InputLabel sx={{ pt: 1, color: "red" }}>{defaultPostError}</InputLabel>
            </Box>
            <Box mt={4} mb={1}>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  backgroundColor: appColor,
                  color: "#FFFFFF",
                  ":hover": {
                    backgroundColor: appColor
                  }
                }}
                fullWidth
                disabled={isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Upload Post"}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
    </DashboardLayout>
  ) : (
    <DashboardLayout>
      <DashboardNavbar />
      <Box
        mb={3}
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          position: "relative"
        }}
      >
        <Button
          sx={{
            color: "#59D6DF",
            border: "1px solid grey",
            right: 0,
            position: "absolute",
            top: 2
          }}
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Add/Update Brodcast
        </Button>
      </Box>
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "10px"
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 5,
              top: 5
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="h2" gutterBottom>
            {id !== "" ? "Update Broadcast" : "Add Broadcast"}
          </Typography>
          <form autoComplete="off" onSubmit={handleSubmit}>
            <Box mb={2}>
              <TextField
                fullWidth
                label="Admin Name"
                value={adminName}
                variant="outlined"
                margin="normal"
                onChange={(e) => {
                  setAdminName(e.target.value);
                  setAdminNameError("");
                }}
              />
              <InputLabel sx={{ pt: 1, color: "red" }}>{adminNameError}</InputLabel>
            </Box>
            <Box mb={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  component="label"
                  variant=""
                  sx={{
                    border: "1px solid rgba(0,0,0,0.2)",
                    borderRadius: 1,
                    py: 1.5,
                    justifyContent: "start",
                    color: "rgba(0,0,0,0.5)",
                    fontWeight: 450,
                    textTransform: "none",
                    fontSize: "13px",
                    width: preview ? "90%" : "100%"
                  }}
                  tabIndex={-1}
                >
                  Upload Admin Profile
                  <VisuallyHiddenInput
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    onChange={handleAdminProfileFileChange}
                  />
                </Button>
                {preview && (
                  <Box mt={2} display="flex" justifyContent="center" sx={{ position: "relative" }}>
                    <Avatar
                      src={preview}
                      alt="Admin Profile"
                      sx={{ width: 30, height: 30, position: "absolute", bottom: 5 }}
                    />
                  </Box>
                )}
              </Box>
              <InputLabel sx={{ pt: 1, color: "red" }}>{adminProfileError}</InputLabel>
            </Box>
            <Box mb={2}>
              <TextField
                fullWidth
                label="Post Content"
                variant="outlined"
                rows={3}
                value={postContent}
                multiline
                sx={{
                  mt: 2,
                  "& .MuiInputLabel-root": {
                    backgroundColor: "white",
                    padding: "4px"
                  },
                  "& .MuiInputBase-input": {
                    p: "0px !important"
                  },
                  "& .MuiInputBase-root": {
                    pt: "15px !important",
                    px: 2
                  }
                }}
                onChange={(e) => {
                  setPostContent(e.target.value);
                  setPostContentError("");
                }}
              />
              <InputLabel sx={{ pt: 1, color: "red" }}>{postContentError}</InputLabel>
            </Box>
            <Box mb={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  component="label"
                  variant=""
                  sx={{
                    border: "1px solid rgba(0,0,0,0.2)",
                    borderRadius: 1,
                    py: 1.5,
                    justifyContent: "start",
                    color: "rgba(0,0,0,0.5)",
                    fontWeight: 450,
                    fontSize: "13px",
                    textTransform: "none",
                    width: postPreview ? "90%" : "100%"
                  }}
                  tabIndex={-1}
                >
                  Upload Admin Post
                  <VisuallyHiddenInput
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    onChange={handleAdminPostFileChange}
                  />
                </Button>
                {postPreview && (
                  <Box mt={2} display="flex" justifyContent="center" sx={{ position: "relative" }}>
                    <Avatar
                      src={postPreview}
                      alt="Admin Post"
                      sx={{ width: 30, height: 30, position: "absolute", bottom: 5 }}
                    />
                  </Box>
                )}
              </Box>
              <InputLabel sx={{ pt: 1, color: "red" }}>{defaultPostError}</InputLabel>
            </Box>
            <Box mt={4} mb={1}>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  backgroundColor: appColor,
                  color: "#FFFFFF",
                  ":hover": {
                    backgroundColor: appColor
                  }
                }}
                fullWidth
                disabled={isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Upload Post"}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
    </DashboardLayout>
  );
};

export default Broadcasting;
